import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import tipiContattoService, { TipoContatto } from '@/services/tipiContattoService';
import {
  TIPI_CONTATTO_DEFAULT,
  TipoContattoOption,
  coloreTipoContatto,
  tipoContattoLabelFrom,
} from '@/constants/tipiContatto';

export const TIPI_CONTATTO_QUERY_KEY = 'tipi-contatto';

/**
 * Elenco dei tipi contatto configurati dall'utente.
 * Finché la chiamata non risponde (o se fallisce) restituisce i tipi standard,
 * così le select non restano mai vuote.
 */
export const useTipiContatto = (onlyActive = true) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: [TIPI_CONTATTO_QUERY_KEY, onlyActive],
    queryFn: () => tipiContattoService.getTipiContatto(onlyActive),
    staleTime: 5 * 60 * 1000,
  });

  const tipi: TipoContattoOption[] = useMemo(() => {
    if (!data || data.length === 0) return TIPI_CONTATTO_DEFAULT;
    return data.map((t: TipoContatto) => ({
      value: t.codice,
      label: t.nome,
      colore: t.colore,
    }));
  }, [data]);

  const label = useCallback((value?: string) => tipoContattoLabelFrom(tipi, value), [tipi]);

  const colore = useCallback(
    (value?: string) => coloreTipoContatto(value, tipi.find((t) => t.value === value)?.colore),
    [tipi]
  );

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [TIPI_CONTATTO_QUERY_KEY] }),
    [queryClient]
  );

  return { tipi, isLoading, error, label, colore, invalidate };
};

export default useTipiContatto;
